import type { DateString, Identifiable, Nominal, Nullable, Replace, Slug, UUID } from '../../lib/typeHelpers.no-deps'
import type { Procedure } from '../campaigns/types.no-deps'
import type * as Flows from '../flows/types.no-deps'
import type * as FlowTriggers from '../flowTriggers/types.no-deps'
import type { ReportStatusFlow } from '../reportStatusFlow/types.no-deps'
import type { ScheduleWithAssignees } from '../schedule/types.no-deps'
import type { FlowElementTag, FlowChoiceTag, WorkflowChoiceTag } from '../tags/types.no-deps'
import type { InsertableWorkflowParameter, WorkflowParameter } from '../workflowParameter/types.no-deps'

export type WorkflowType = 'guide' | 'task' | 'training'
export const type: WorkflowType[] = ['guide', 'task', 'training']

export interface Workflow {
    id: number
    type: WorkflowType
    name: Slug
    isDeleted: boolean
    lastModified: Nullable<Date>
    lastModifiedBy: Nullable<number>
    flow: Nullable<Flows.Flow>
    numberOfVersions: Nullable<number>
    requiresSignature: boolean
    displaysTextContentInReports: boolean
}

export type WorkflowWithResolvedName = Replace<Workflow, { name: { text: string; slug?: Slug } }>
export type WorkflowInfo = Replace<WorkflowWithResolvedName, { lastModified: string }>

export interface WorkflowActivity {
    id: number
    position: number
    dependencies: number[]
}

export interface ActivityWorkflow extends Omit<Workflow, 'flow' | 'lastModified' | 'lastModifiedBy'> {
    activities: WorkflowActivity[]
    procedure: Nullable<Procedure>
}

export interface SyncFlowWorkflow extends Omit<Workflow, 'lastModified' | 'lastModifiedBy'> {
    /** @deprecated Can remove after all apps are above a75.0 */
    adHoc: boolean
    procedure: Nullable<Procedure>
    trainingRecommendations: number[]
    workflowParameters: WorkflowParameter[]
}

export interface AppFlowWorkflow extends Omit<Workflow, 'lastModified' | 'lastModifiedBy'> {
    procedure: Nullable<Procedure>
    trainingRecommendations?: number[]
    workflowParameters?: WorkflowParameter[]
}

export interface WorkflowVersionIdentifier {
    id: number
    version?: number
}

export type WorkflowReference = AppFlowWorkflow | WorkflowVersionIdentifier

export function isAppFlowWorkflow(workflow: WorkflowReference): workflow is AppFlowWorkflow {
    return 'flow' in workflow
}

export function hasVersion(workflow: WorkflowReference): workflow is WorkflowVersionIdentifier {
    return 'version' in workflow
}

export function getVersionIdentifier(workflow: WorkflowReference): WorkflowVersionIdentifier {
    if (hasVersion(workflow)) return workflow
    if (workflow.numberOfVersions == null) return null
    return { id: workflow.id, version: workflow.numberOfVersions }
}

export type EditableSchedule = Partial<ScheduleWithAssignees> & {
    id: UUID
    // not to be stored anywhere, this is transient data just for the endpoint to use
    originalSchedule?: Nullable<Partial<ScheduleWithAssignees>>
    submitTime?: Date
}

// Domain
export interface AddInput {
    type: Workflow['type']
    name: Workflow['name']
    flow: Workflow['flow']
    requiresSignature?: Workflow['requiresSignature']
    flowTriggers?: Array<FlowTriggers.Trigger & { requiresSave: boolean }>
    tags?: string[]
    elementTags?: FlowElementTag[]
    choiceTags?: FlowChoiceTag[]
    reportStatuses?: {
        statuses: Array<{ id?: number; name: string; uuid?: UUID }>
        defaultIndex: number
        // Map old status ids to report statuses from the statuses field,
        // referenced by index.
        // Need to use index as will not be ids available for new statuses yet
        remap?: { [id: number]: number }
        flow?: ReportStatusFlow
    }
    schedules?: Nullable<EditableSchedule[]>
    trainingRecommendations?: number[]
    displaysTextContentInReports?: Workflow['displaysTextContentInReports']
    workflowParameters?: InsertableWorkflowParameter[]
}

export interface GetOutput {
    id: Workflow['id']
    type: Workflow['type']
    name: Workflow['name']
    flow: Workflow['flow']
    isActive: boolean
    lastMod: Date
    lastModifiedBy?: Nullable<number>
    activities: WorkflowActivity[]
    flowTriggers?: FlowTriggers.Trigger[]
    schedules?: ScheduleWithAssignees[]
    workflowParameters: InsertableWorkflowParameter[]
    tags?: string[]
    elementTags?: FlowElementTag[]
    choiceTags?: WorkflowChoiceTag[]
    requiresSignature: boolean
    trainingRecommendations: number[]
    displaysTextContentInReports: boolean
}

// Endpoints
export type SyncActivityWorkflowInput = { lastSyncTime: Nullable<DateString> }
export type SyncActivityWorkflowOutput = { workflows: ActivityWorkflow[]; syncTime: DateString }

export type WorkflowError = DuplicateNameError

export interface DuplicateNameError {
    type: 'DuplicateName'
    name: string
}

export interface RemapStatusError {
    type: 'RemapStatus'
    name: string
}

export type WorkflowId = Nominal<number, 'WorkflowId'>
export const assertWorkflowId = (workflowId: number) => workflowId as WorkflowId
export type Sync = {
    id: number
    name: string
    items: Array<
        Identifiable<{
            action: number
            list: number
            position: number
            dependencies: number[]
        }>
    >
    targets: number[]
}

export interface UpsertInput {
    id?: Workflow['id']
    name: Workflow['name']
    type: Workflow['type']
}

export interface UpsertOutput extends UpsertInput {
    id: Workflow['id']
    lastMod: Date
}

export type Dependencies = { entry: number; dependency: number }
